import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import sectionStyles from '/components/Sections/Section.module.css';
import paneStyles from '/components/Panes/InfoPane.module.css';
import styles from './Support.module.css';

const categories = [
  ['technical', 'Something is broken'], ['missions', 'Mission help'], ['content', 'Content problem'],
  ['media', 'Image or video problem'], ['account', 'Account or sign-in'], ['navigation', 'Navigation or layout'],
  ['accessibility', 'Accessibility'], ['other', 'Something else'],
];
const emptyForm = { category: 'technical', severity: 'normal', subject: '', description: '', name: '', email: '' };
const tokenKey = 'shuffledb.support.ticketTokens';

function storedTokens() {
  try { return JSON.parse(window.localStorage.getItem(tokenKey) || '{}'); } catch { return {}; }
}

export default function Support() {
  const router = useRouter();
  const { data: session, status: sessionStatus } = useSession();
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [tab, setTab] = useState('new');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const pageContext = useMemo(() => typeof window === 'undefined' ? '' : String(router.query.from || document.referrer || window.location.href), [router.query.from]);

  const loadTickets = useCallback(async () => {
    if (session?.user) {
      const response = await fetch('/api/support/tickets');
      const data = await response.json();
      if (response.ok) setTickets(data.tickets || []);
      return;
    }
    const tokens = storedTokens();
    const anonymousTickets = await Promise.all(Object.entries(tokens).map(async ([id, token]) => {
      const response = await fetch(`/api/support/tickets/${id}`, { headers: { 'x-support-access-token': token } });
      if (!response.ok) return null;
      const data = await response.json();
      return data.ticket || null;
    }));
    setTickets(anonymousTickets.filter(Boolean).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
  }, [session?.user]);

  const loadTicket = useCallback(async (ticket) => {
    const token = storedTokens()[ticket.id];
    const response = await fetch(`/api/support/tickets/${ticket.id}`, { headers: token ? { 'x-support-access-token': token } : undefined });
    const data = await response.json();
    if (!response.ok) return setMessage(data.message || 'Ticket could not be loaded.');
    setSelected(data.ticket);
    setThread(data.messages || []);
    setTab('tickets');
  }, []);

  useEffect(() => { if (sessionStatus !== 'loading') loadTickets(); }, [loadTickets, sessionStatus]);
  useEffect(() => {
    if (!router.isReady || !router.query.ticket) return;
    loadTicket({ id: String(router.query.ticket) });
  }, [loadTicket, router.isReady, router.query.ticket]);

  const uploadFiles = async (ticketId, messageId, accessToken, selectedFiles) => {
    for (const file of selectedFiles) {
      const body = new FormData(); body.append('file', file); body.append('messageId', String(messageId));
      const response = await fetch(`/api/support/tickets/${ticketId}/attachments`, { method: 'POST', headers: accessToken ? { 'x-support-access-token': accessToken } : undefined, body });
      if (!response.ok) { const data = await response.json(); throw new Error(data.message || `${file.name} could not be attached.`); }
    }
  };

  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const response = await fetch('/api/support/tickets', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, pageUrl: pageContext, browserInfo: navigator.userAgent, context: { viewport: `${window.innerWidth}x${window.innerHeight}`, route: router.asPath } }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ticket could not be created.');
      if (data.accessToken) {
        const tokens = storedTokens(); tokens[data.ticket.id] = data.accessToken;
        window.localStorage.setItem(tokenKey, JSON.stringify(tokens));
      }
      await uploadFiles(data.ticket.id, data.messageId, data.accessToken, files);
      setForm(emptyForm); setFiles([]);
      setMessage(`${data.ticket.number} was created. Keep this reference for follow-up.`);
      await loadTickets();
      await loadTicket(data.ticket);
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };

  const postReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true); setMessage('');
    const token = storedTokens()[selected.id];
    const response = await fetch(`/api/support/tickets/${selected.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { 'x-support-access-token': token } : {}) }, body: JSON.stringify({ body: reply }) });
    const data = await response.json();
    if (response.ok) { setReply(''); await loadTicket(selected); }
    else setMessage(data.message || 'Reply could not be sent.');
    setBusy(false);
  };

  return (
    <section className={`${sectionStyles.section} ${styles.page}`}>
      <div className={`${paneStyles.infoPane} ${styles.hero}`}>
        <div><span>HELP &amp; SUPPORT</span><h1>Tell us what went wrong.</h1><p>Report broken functionality, request help, or explain something that blocked your work. Content discussions, correction missions, and media-rights reports remain separate.</p></div>
        <div className={styles.routeContext}><small>PAGE CONTEXT</small><strong>{pageContext || 'Direct support visit'}</strong></div>
      </div>
      <div className={styles.tabs}>
        <button type="button" data-active={tab === 'new'} onClick={() => setTab('new')}>Create a ticket</button>
        <button type="button" data-active={tab === 'tickets'} onClick={() => setTab('tickets')}>My tickets {tickets.length ? `(${tickets.length})` : ''}</button>
      </div>
      {message && <p className={styles.notice} role="status">{message}</p>}
      {tab === 'new' ? (
        <form className={styles.form} onSubmit={submit}>
          <div className={styles.formIntro}><span>01 · IDENTIFY</span><h2>What do you need help with?</h2><p>Choose the closest category. The current page and browser information will be included automatically.</p></div>
          <label>Category<select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>{categories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Impact<select value={form.severity} onChange={(event) => setForm({ ...form, severity: event.target.value })}><option value="minor">Minor inconvenience</option><option value="normal">Needs attention</option><option value="blocking">Blocking my work</option><option value="urgent">Urgent access or safety issue</option></select></label>
          <label className={styles.wide}>Short summary<input required maxLength="255" value={form.subject} onChange={(event) => setForm({ ...form, subject: event.target.value })} placeholder="What happened?" /></label>
          <label className={styles.wide}>What were you trying to do?<textarea required maxLength="12000" rows="8" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Include what you expected, what happened instead, and any steps that reproduce it." /></label>
          {!session?.user && <><label>Your name<input maxLength="255" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>Email address<input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label></>}
          <label className={styles.wide}>Screenshots, short videos, PDFs, or logs<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,application/pdf,text/plain" onChange={(event) => setFiles([...event.target.files].slice(0, 4))} /><small>Up to four files, 25 MB each. Attachments are private to you and ShuffleDB administrators.</small>{files.length > 0 && <span>{files.map((file) => file.name).join(' · ')}</span>}</label>
          <button className={styles.submit} type="submit" disabled={busy}>{busy ? 'Creating ticket…' : 'Create support ticket'}</button>
        </form>
      ) : (
        <div className={styles.ticketWorkspace}>
          <aside className={styles.ticketList}>
            {!session?.user && !selected && <p>Sign in to see tickets across devices. Anonymous tickets remain available in this browser immediately after submission.</p>}
            {session?.user && !tickets.length && <p>You have not created any support tickets.</p>}
            {tickets.map((ticket) => <button key={ticket.id} type="button" data-active={selected?.id === ticket.id} onClick={() => loadTicket(ticket)}><span>{ticket.number}</span><strong>{ticket.subject}</strong><small>{ticket.status.replaceAll('_', ' ')} · {new Date(ticket.updatedAt).toLocaleDateString()}</small></button>)}
          </aside>
          <main className={styles.thread}>
            {!selected ? <div className={styles.noSelection}><h2>Select a ticket</h2><p>Its conversation and current status will appear here.</p></div> : <>
              <header><div><span>{selected.number}</span><h2>{selected.subject}</h2></div><strong>{selected.status.replaceAll('_', ' ')}</strong></header>
              <div className={styles.messages}>{thread?.map((item) => <article key={item.id} data-role={item.role}><div><strong>{item.author}</strong><small>{new Date(item.createdAt).toLocaleString()}</small></div><p>{item.body}</p>{item.attachments.map((attachment) => { const token = storedTokens()[selected.id]; return <a key={attachment.id} href={`${attachment.url}${token ? `?accessToken=${encodeURIComponent(token)}` : ''}`} target="_blank" rel="noreferrer">{attachment.name} ↗</a>; })}</article>)}</div>
              {selected.status !== 'closed' && <div className={styles.reply}><textarea rows="5" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Reply to ShuffleDB Support…" /><button type="button" disabled={busy || !reply.trim()} onClick={postReply}>Send reply</button></div>}
            </>}
          </main>
        </div>
      )}
      <p className={styles.routingHelp}>Need something else? <Link href="/Missions">Propose a content improvement through missions</Link> or <Link href="/DMCA">report a media-rights concern</Link>.</p>
    </section>
  );
}
